/**
 * What a terminal last said about its Sync Gateway replication, in words — for Terminaler's «Synk» column and the
 * Resync dialog. The figures come from CagistaServices /api/admin/terminalSyncStates (TerminalSyncStateService), which
 * the terminal reports with every order-sync poll from TastyPOS 1.6.33.
 *
 * Why it exists (2026-10-16): mollapizza's KASSE 1 replicated nothing for hours and nothing on any page said so; the
 * fallback sync rescued every order, so the till looked fine.
 *
 * `rank` orders the column worst-first when sorted ascending.
 *
 * A till or kiosk that is off Sync Gateway still gets its orders to the server through the fallback sync; a kitchen
 * screen (KDS) has none, and shows no new orders until it is back — so the words differ.
 */

/** Not connected for less than this is reconnecting, not a problem yet (the server alerts after five minutes). */
const RECONNECTING_MINUTES = 5;

class TerminalSyncStatus {
    /**
     * @param state      one entry of `terminals` from the endpoint, or undefined
     * @param serverTime the response's `serverTime` — durations are measured on the server's clock, not the browser's
     * @param details    SUPER_ADMIN: add the technical line
     * @param terminalType POS, KIOSK or KDS
     * @returns {{tone: string, label: string, detail: string, tech: string, rank: number}}
     */
    describe(state, serverTime, details, terminalType) {
        if (!state || !state.known) {
            return { tone: 'muted', label: 'Ukjent', detail: 'Har ikke rapportert ennå', tech: '', rank: 3 };
        }
        if (state.stale) {
            return { tone: 'bad', label: 'Ingen kontakt', detail: 'Sist hørt ' + this.clock(state.reportedAt, serverTime),
                tech: details ? this.tech(state, serverTime) : '', rank: 1 };
        }
        if (!state.reports) {
            return { tone: 'muted', label: 'Rapporterer ikke',
                detail: (state.appVersion ? state.appVersion + ' — ' : '') + 'eldre app, oppdater for å se synk-status',
                tech: '', rank: 2 };
        }
        const extra = [];
        if (state.connected && state.pendingPush > 0) {
            extra.push(state.pendingPush + ' venter på å bli sendt');
        }
        if (state.resync === 'WAITING') {
            extra.push('resync pågår');
        }
        const tech = details ? this.tech(state, serverTime) : '';
        if (state.connected) {
            return { tone: 'ok', label: 'Tilkoblet',
                detail: ['siden ' + this.clock(state.since, serverTime)].concat(extra).join(' · '), tech, rank: 4 };
        }
        const minutes = this.minutesBetween(state.since, serverTime);
        const reconnecting = minutes !== null && minutes < RECONNECTING_MINUTES;
        return {
            tone: reconnecting ? 'warn' : 'bad',
            label: reconnecting ? 'Kobler til' : 'Ikke tilkoblet',
            detail: [(state.since ? 'siden ' + this.clock(state.since, serverTime) : 'ukjent hvor lenge'),
                terminalType === 'KDS' ? 'viser ikke nye ordre' : 'reserve-synken tar ordrene'].concat(extra).join(' · '),
            tech,
            rank: 0
        };
    }

    /** SUPER_ADMIN's line: which login it replicates with, rebuilds, the last errors. */
    tech(state, serverTime) {
        const parts = [];
        if (state.credential === 'device') parts.push('egen bruker');
        if (state.credential === 'merchant') parts.push('merchant-innlogging (reserve)');
        if (state.rebuilds > 0) parts.push('bygd på nytt ' + state.rebuilds + (state.rebuilds === 1 ? ' gang' : ' ganger'));
        // With its time: under «Tilkoblet» an error from an outage that is over read as a problem now (2026-10-19).
        if (state.lastError) {
            parts.push('siste feil' + (state.lastErrorAt ? ' ' + this.clock(state.lastErrorAt, serverTime) : '') + ': ' + state.lastError);
        }
        if (state.docErrors > 0 && state.lastDocError) parts.push('dokumentfeil: ' + state.lastDocError);
        return parts.join(' · ');
    }

    /** «14:02» today, «15.10 14:02» another day — in the browser's own time zone. */
    clock(iso, serverTime) {
        const at = new Date(iso);
        if (!iso || isNaN(at.getTime())) return '?';
        const ref = new Date(serverTime);
        const pad = (n) => String(n).padStart(2, '0');
        const time = pad(at.getHours()) + ':' + pad(at.getMinutes());
        const sameDay = !isNaN(ref.getTime()) && ref.toDateString() === at.toDateString();
        return sameDay ? time : pad(at.getDate()) + '.' + pad(at.getMonth() + 1) + ' ' + time;
    }

    minutesBetween(fromIso, toIso) {
        const from = Date.parse(fromIso);
        const to = Date.parse(toIso);
        if (isNaN(from) || isNaN(to)) return null;
        return Math.max(0, Math.floor((to - from) / 60000));
    }
}

export default new TerminalSyncStatus();
